import React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  VerifiedUser as VerifiedUserIcon,
  ContactMail as ContactMailIcon,
  Gavel as GavelIcon,
  AccountBalance as AccountBalanceIcon,
  AccessTime as AccessTimeIcon,
  MonetizationOn,
} from "@mui/icons-material";

interface WelcomeStepProps {
  onNext: () => void;
}

const setupItems = [
  {
    icon: <ContactMailIcon />,
    title: "Add your address & phone",
    description: "So we know where you're located",
  },
  {
    icon: <VerifiedUserIcon />,
    title: "Verify your identity",
    description: "A quick photo ID check (~2 min)",
  },
  {
    icon: <GavelIcon />,
    title: "Review our terms",
    description: "Understand your rights and obligations",
  },
  {
    icon: <AccountBalanceIcon />,
    title: "Link a bank account",
    description: "To receive payouts from your circles",
  },
];

export const WelcomeStep = ({ onNext }: WelcomeStepProps) => {
  const theme = useTheme();

  return (
    <Stack spacing={4} alignItems="center">
      {/* Hero section */}
      <Box sx={{ textAlign: "center" }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <MonetizationOn sx={{ fontSize: 60, color: theme.palette.primary.main }} />
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Let's get you set up
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: 420, mx: "auto" }}
        >
          We need a few things to keep our community safe and get you ready to
          join circles.
        </Typography>
      </Box>

      {/* What to expect */}
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Typography
          variant="overline"
          color="text.secondary"
          fontWeight={600}
          sx={{ letterSpacing: 1.2 }}
        >
          Here's what we'll do
        </Typography>

        {setupItems.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            >
              {item.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {item.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {item.description}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.disabled",
                fontWeight: 600,
                minWidth: 20,
                textAlign: "center",
              }}
            >
              {index + 1}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Estimated time */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: alpha(theme.palette.info.main, 0.06),
          border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
        }}
      >
        <AccessTimeIcon
          sx={{ fontSize: 18, color: theme.palette.info.main }}
        />
        <Typography variant="body2" color="text.secondary">
          This takes about <strong>5 minutes</strong>
        </Typography>
      </Box>

      {/* CTA */}
      <Button
        variant="contained"
        size="large"
        onClick={onNext}
        endIcon={<ArrowForwardIcon />}
        fullWidth
        sx={{
          py: 1.5,
          fontSize: "1rem",
          fontWeight: 600,
          background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
          "&:hover": {
            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
          },
        }}
      >
        Let's Go
      </Button>
    </Stack>
  );
};
